export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export interface RequestLike {
  headers: Record<string, string | string[] | undefined>
  method?: string
  url?: string
  rawHeaders?: string[]
}

export interface LogEntry {
  timestamp: number
  level: LogLevel
  message: string
  metadata?: Record<string, unknown>
  sessionId?: string
  url?: string
  userAgent?: string
  origin?: 'browser' | 'server'
}

export interface MetricEntry {
  id: string
  usecase: string
  duration: number
  success: boolean
  metadata?: Record<string, unknown>
  timestamp?: number
}

/**
 * Estamos asumiendo por ahora que el tipo es COUNT,
 * en el futuro tendríamos que agregar una propiedad type (GAUGE, COUNTER, HISTOGRAM)
 */
export interface CustomMetricEntry {
  id: string
  name: string
  metadata?: Record<string, unknown>
  timestamp?: number
}

export function isCustomMetricEntry(
  metric: MetricEntry | CustomMetricEntry
): metric is CustomMetricEntry {
  return 'name' in metric
}

export function isMetricEntry(metric: MetricEntry | CustomMetricEntry): metric is MetricEntry {
  return 'usecase' in metric
}

export interface UnifiedPayload {
  logs?: LogEntry[]
  metrics?: (MetricEntry | CustomMetricEntry)[]
}

export abstract class MonitoringClient {
  abstract info({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): void

  abstract warn({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): void

  abstract error({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): void

  abstract debug({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): void

  abstract sendMetric(metric: MetricEntry | CustomMetricEntry): void
}

export interface ILogger {
  info({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): Promise<void>

  warn({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): Promise<void>

  error({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): Promise<void>

  debug({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): Promise<void>
}

export interface IMetrics {
  send(metric: MetricEntry | CustomMetricEntry): Promise<void>
}
